import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers/display-toast';
import { patchOrganizationExternalRates } from '@/modules/common/api/organization.api';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { RateSource } from '@/modules/common/types/rate-source';
import {
  invalidateOrganizationDefaultsQueries,
  invalidateOrganizationSummaryQueries,
} from '@/modules/common/utils/query-invalidation';

interface UpdateExternalRatesParams {
  defaultCurrencyCode: string;
  defaultProjectRateSource: RateSource;
  defaultRate: number;
}

export const useUpdateExternalRates = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.settings');
  const { getErrorMessage } = useApiError();

  return useMutation({
    mutationFn: (params: UpdateExternalRatesParams) => patchOrganizationExternalRates(params),
    onSuccess: async () => {
      await invalidateOrganizationDefaultsQueries(queryClient);
      await invalidateOrganizationSummaryQueries(queryClient);

      displayToastSuccess(t('pages.finance.success.updated'));
    },
    onError: error => {
      displayToastError(getErrorMessage(error, true));
    },
  });
};
